import React, { useEffect, useState, useMemo } from "react";
import { listInsiderTrades } from "../../api/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import PageBackButton from "../../components/PageBackButton";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../components/ui/select";
import { ChevronLeft, ChevronRight, ExternalLink, ArrowUpCircle, ArrowDownCircle, Gift, RefreshCw } from "lucide-react";
import {
  BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  Cell,
} from "recharts";

const TICKER_PRESETS = [
  "AAPL", "MSFT", "GOOGL", "AMZN", "NVDA", "META", "TSLA",
  "JPM", "GS", "V", "UNH", "JNJ", "XOM", "BA",
];

const TRANSACTION_TYPES = [
  { value: "", label: "All" },
  { value: "P", label: "Purchase" },
  { value: "S", label: "Sale" },
  { value: "A", label: "Award" },
  { value: "C", label: "Conversion" },
  { value: "D", label: "Disposition" },
  { value: "G", label: "Gift" },
  { value: "X", label: "Expiration" },
  { value: "O", label: "Other" },
];

const OWNER_TYPES = [
  { value: "", label: "All" },
  { value: "director", label: "Director" },
  { value: "officer", label: "Officer" },
  { value: "10percent", label: "10% Owner" },
  { value: "other", label: "Other" },
];

function getDefaultDateRange() {
  const now = new Date();
  const oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(oneMonthAgo), to: fmt(now) };
}

function formatCurrency(value) {
  if (value == null) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(2)}B`;
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toFixed(2)}`;
}

function formatNumber(value) {
  if (value == null) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function TransactionBadge({ type, display }) {
  const config = {
    P: { bg: "bg-green-500/15", text: "text-green-400", icon: <ArrowUpCircle className="w-3.5 h-3.5" /> },
    S: { bg: "bg-red-500/15", text: "text-red-400", icon: <ArrowDownCircle className="w-3.5 h-3.5" /> },
    A: { bg: "bg-blue-500/15", text: "text-blue-400", icon: <Gift className="w-3.5 h-3.5" /> },
    C: { bg: "bg-yellow-500/15", text: "text-yellow-400", icon: <RefreshCw className="w-3.5 h-3.5" /> },
    D: { bg: "bg-orange-500/15", text: "text-orange-400", icon: <ArrowDownCircle className="w-3.5 h-3.5" /> },
    G: { bg: "bg-purple-500/15", text: "text-purple-400", icon: <Gift className="w-3.5 h-3.5" /> },
  };
  const c = config[type] || { bg: "bg-gray-500/15", text: "text-gray-400", icon: null };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
      {c.icon}
      {display || type}
    </span>
  );
}

function OwnerBadge({ type, display }) {
  const config = {
    officer: "bg-indigo-500/15 text-indigo-400",
    director: "bg-cyan-500/15 text-cyan-400",
    "10percent": "bg-amber-500/15 text-amber-400",
  };
  const cls = config[type] || "bg-gray-500/15 text-gray-400";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {display || type}
    </span>
  );
}

export default function InsiderTradingPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trades, setTrades] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [ticker, setTicker] = useState("");
  const [insiderName, setInsiderName] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [ownerType, setOwnerType] = useState("");
  const [minValue, setMinValue] = useState("");

  // View mode
  const [viewMode, setViewMode] = useState("table");

  useEffect(() => {
    const { from, to } = getDefaultDateRange();
    setDateFrom(from);
    setDateTo(to);
    fetchTrades({ dateFrom: from, dateTo: to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTrades = async (overrides = {}) => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        ticker: overrides.ticker ?? ticker,
        insider_name: overrides.insiderName ?? insiderName,
        transaction_type: overrides.transactionType ?? transactionType,
        owner_type: overrides.ownerType ?? ownerType,
        date_from: overrides.dateFrom ?? dateFrom,
        date_to: overrides.dateTo ?? dateTo,
        min_value: overrides.minValue ?? minValue,
        page: overrides.page ?? page,
        page_size: pageSize,
      };
      const res = await listInsiderTrades(params);
      if (res && typeof res.count === "number" && Array.isArray(res.results)) {
        setTrades(res.results);
        setTotalCount(res.count);
      } else {
        const items = Array.isArray(res) ? res : [];
        setTrades(items);
        setTotalCount(items.length);
      }
    } catch (e) {
      setError(e.message || "Failed to load insider trading data");
    } finally {
      setLoading(false);
    }
  };

  const handlePresetClick = (preset) => {
    setTicker(preset);
    setPage(1);
    fetchTrades({ ticker: preset, page: 1 });
  };

  const handleApply = () => {
    setPage(1);
    fetchTrades({ page: 1 });
  };

  const handleClearFilters = () => {
    setTicker("");
    setInsiderName("");
    setTransactionType("");
    setOwnerType("");
    setMinValue("");
    const { from, to } = getDefaultDateRange();
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
    fetchTrades({ ticker: "", insiderName: "", transactionType: "", ownerType: "", minValue: "", dateFrom: from, dateTo: to, page: 1 });
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    fetchTrades({ page: p });
  };

  // Chart data — aggregate by ticker for top-N summary
  const chartData = useMemo(() => {
    const byTicker = {};
    trades.forEach((t) => {
      if (!byTicker[t.ticker]) {
        byTicker[t.ticker] = { ticker: t.ticker, purchases: 0, sales: 0, totalValue: 0 };
      }
      const val = parseFloat(t.total_value) || 0;
      if (t.transaction_type === "P") byTicker[t.ticker].purchases += val;
      else if (t.transaction_type === "S") byTicker[t.ticker].sales += val;
      byTicker[t.ticker].totalValue += val;
    });
    return Object.values(byTicker)
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 20);
  }, [trades]);

  // Summary stats
  const stats = useMemo(() => {
    let totalPurchases = 0, totalSales = 0, purchaseCount = 0, saleCount = 0;
    trades.forEach((t) => {
      const val = parseFloat(t.total_value) || 0;
      if (t.transaction_type === "P") { totalPurchases += val; purchaseCount++; }
      else if (t.transaction_type === "S") { totalSales += val; saleCount++; }
    });
    return { totalPurchases, totalSales, purchaseCount, saleCount };
  }, [trades]);

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start gap-3">
        <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
        <div>
          <h1 className="text-2xl font-semibold">Insider Trading</h1>
          <p className="text-sm text-textSecondary">
            SEC Form 4 insider trading filings — purchases, sales, awards, and other transactions by corporate insiders
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs text-textSecondary mb-1">Total Records</p>
          <p className="text-xl font-semibold">{totalCount.toLocaleString()}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-textSecondary mb-1">Purchases (page)</p>
          <p className="text-xl font-semibold text-green-400">{stats.purchaseCount} · {formatCurrency(stats.totalPurchases)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-textSecondary mb-1">Sales (page)</p>
          <p className="text-xl font-semibold text-red-400">{stats.saleCount} · {formatCurrency(stats.totalSales)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-textSecondary mb-1">Net Buy/Sell (page)</p>
          <p className={`text-xl font-semibold ${stats.totalPurchases - stats.totalSales >= 0 ? "text-green-400" : "text-red-400"}`}>
            {formatCurrency(stats.totalPurchases - stats.totalSales)}
          </p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm mb-1">Ticker</label>
            <Input
              placeholder="e.g. AAPL"
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Insider Name</label>
            <Input
              placeholder="Search by name..."
              value={insiderName}
              onChange={(e) => setInsiderName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm mb-1">Transaction Type</label>
            <Select value={transactionType} onValueChange={(val) => setTransactionType(val)} placeholder="All">
              <SelectTrigger className="w-full">Transaction Type</SelectTrigger>
              <SelectContent>
                {TRANSACTION_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm mb-1">Owner Type</label>
            <Select value={ownerType} onValueChange={(val) => setOwnerType(val)} placeholder="All">
              <SelectTrigger className="w-full">Owner Type</SelectTrigger>
              <SelectContent>
                {OWNER_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm mb-1">Min Value ($)</label>
            <Input
              type="number"
              placeholder="e.g. 100000"
              value={minValue}
              onChange={(e) => setMinValue(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2">Quick Select</label>
          <div className="flex flex-wrap gap-2">
            {TICKER_PRESETS.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant={ticker === preset ? "default" : "secondary"}
                size="sm"
                onClick={() => handlePresetClick(preset)}
              >
                {preset}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleApply}>Apply</Button>
          <Button variant="secondary" onClick={handleClearFilters}>Clear Filters</Button>
          <div className="ml-auto flex gap-2">
            <Button
              variant={viewMode === "table" ? "default" : "secondary"}
              size="sm"
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              variant={viewMode === "chart" ? "default" : "secondary"}
              size="sm"
              onClick={() => setViewMode("chart")}
            >
              Chart
            </Button>
          </div>
        </div>
      </Card>

      {/* Chart View */}
      {viewMode === "chart" && (
        <Card className="p-4 space-y-4">
          <h2 className="text-lg font-medium">Buy vs Sell Volume by Ticker</h2>
          {chartData.length === 0 ? (
            <p className="text-textSecondary text-sm">
              {loading ? "Loading..." : "No data to chart. Apply filters to see results."}
            </p>
          ) : (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 60, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="ticker"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => {
                      if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
                      if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
                      if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
                      return `$${v}`;
                    }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155" }}
                    labelStyle={{ color: "#94a3b8" }}
                  />
                  <Legend />
                  <Bar dataKey="purchases" name="Purchases" fill="#10b981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="sales" name="Sales" fill="#ef4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Ticker</th>
                  <th className="text-left p-3">Insider</th>
                  <th className="text-left p-3">Role</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-right p-3">Shares</th>
                  <th className="text-right p-3">Price</th>
                  <th className="text-right p-3">Value</th>
                  <th className="text-right p-3">Holdings After</th>
                  <th className="text-center p-3">Filing</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td className="p-3" colSpan={10}>Loading...</td></tr>
                )}
                {error && !loading && (
                  <tr><td className="p-3 text-red-400" colSpan={10}>{error}</td></tr>
                )}
                {!loading && !error && trades.length === 0 && (
                  <tr><td className="p-3 text-textSecondary" colSpan={10}>No insider trades found. Try adjusting your filters.</td></tr>
                )}
                {!loading && !error && trades.map((t) => (
                  <tr key={t.id} className="border-t border-border/40 hover:bg-secondary/20">
                    <td className="p-3 whitespace-nowrap">{t.transaction_date}</td>
                    <td className="p-3 font-medium">{t.ticker}</td>
                    <td className="p-3">
                      <div className="max-w-[200px] truncate" title={t.insider_name}>
                        {t.insider_name}
                      </div>
                      {t.insider_title && (
                        <div className="text-xs text-textSecondary truncate" title={t.insider_title}>
                          {t.insider_title}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <OwnerBadge type={t.owner_type} display={t.owner_type_display} />
                    </td>
                    <td className="p-3">
                      <TransactionBadge type={t.transaction_type} display={t.transaction_type_display} />
                    </td>
                    <td className="p-3 text-right tabular-nums">{formatNumber(t.shares)}</td>
                    <td className="p-3 text-right tabular-nums">
                      {t.price_per_share ? `$${parseFloat(t.price_per_share).toFixed(2)}` : "—"}
                    </td>
                    <td className={`p-3 text-right font-medium tabular-nums ${
                      t.transaction_type === "P" ? "text-green-400" :
                      t.transaction_type === "S" ? "text-red-400" : ""
                    }`}>
                      {formatCurrency(t.total_value)}
                    </td>
                    <td className="p-3 text-right tabular-nums">{formatNumber(t.shares_owned_after)}</td>
                    <td className="p-3 text-center">
                      {t.filing_url ? (
                        <a
                          href={t.filing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs"
                        >
                          SEC <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
              <span className="text-sm text-textSecondary">
                {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} records
              </span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goToPage(1)} className="px-2">First</Button>
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goToPage(page - 1)} className="px-2">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {(() => {
                  const pages = [];
                  let start = Math.max(1, page - 2);
                  let end = Math.min(totalPages, start + 4);
                  start = Math.max(1, end - 4);
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <Button key={i} variant={i === page ? "default" : "ghost"} size="sm" className="px-3 min-w-[2rem]" onClick={() => goToPage(i)}>{i}</Button>
                    );
                  }
                  return pages;
                })()}
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => goToPage(page + 1)} className="px-2">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => goToPage(totalPages)} className="px-2">Last</Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
